import React, { useState, useEffect, useMemo } from 'react';
import { fetchCustomers, createCustomer, updateCustomer, deleteCustomer } from '../models/CustomersService';
import { fetchInvoices } from '../models/InvoiceService';
import PageShell, { Btn, fmtEUR } from '../components/design/PageShell';
import { Ic } from '../components/design/Icons';

const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (v.toDate) return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const parsePrice = (p) => {
  if (typeof p === 'number') return p;
  if (typeof p === 'string') return parseFloat(p.replace(',', '.')) || 0;
  return 0;
};

const invoiceTotal = (inv) => {
  if (!inv.products || !Array.isArray(inv.products)) return 0;
  return inv.products.reduce((a, p) => a + (Number(p.quantity) || 0) * parsePrice(p.unitPrice), 0);
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cs, invs] = await Promise.all([fetchCustomers(), fetchInvoices()]);
        setCustomers(cs);
        setInvoices(invs);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const enriched = useMemo(() => {
    return customers.map(c => {
      const list = invoices.filter(i => {
        const name = typeof i.client === 'string' ? i.client : (i.client?.Title || '');
        return name === c.Title || name === c.name || i.clientId === c.id;
      });
      const ca = list.reduce((a, i) => a + invoiceTotal(i), 0);
      return { ...c, facturesCount: list.length, ca };
    });
  }, [customers, invoices]);

  const filtered = enriched.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.Title || c.name || '').toLowerCase().includes(q) ||
      (c.City || c.city || '').toLowerCase().includes(q);
  });

  const stats = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const totalCA = enriched.reduce((a, c) => a + c.ca, 0);
    const newThisQ = customers.filter(c => {
      const d = toDate(c.created);
      if (!d) return false;
      const q = Math.floor(now.getMonth() / 3);
      const qS = new Date(year, q * 3, 1);
      return d >= qS;
    });
    return { totalCA, newQ: newThisQ.length };
  }, [enriched, customers]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer ${c.Title || c.name} ?`)) return;
    try {
      await deleteCustomer(c.id);
      setCustomers(customers.filter(x => x.id !== c.id));
      setSelected(null);
    } catch (e) { console.error(e); }
  };

  return (
    <PageShell
      section="clients"
      number="04"
      title="Clients"
      subtitle="Répertoire et historique commercial"
      actions={<Btn kind="primary" icon="plus" onClick={() => { setEditing(null); setFormOpen(true); }}>Ajouter un client</Btn>}
    >
      <div className="ta-filters">
        <div className="ta-search">
          <span className="ta-search-ic">{Ic.search}</span>
          <input placeholder="Rechercher un client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <section className="ta-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div>
          <div className="eyebrow">{"// "}Total</div>
          <div className="f">{customers.length}</div>
          <div className="d">clients actifs</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}CA cumulé</div>
          <div className="f tnum" style={{ fontSize: 36, color: 'var(--accent)' }}>{fmtEUR(stats.totalCA)}</div>
          <div className="d tnum">€ · tous clients</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}Nouveau (T en cours)</div>
          <div className="f" style={{ color: 'var(--ok)' }}>{stats.newQ}</div>
          <div className="d">clients</div>
        </div>
      </section>

      {loading && <div className="mono" style={{ color: 'var(--ink-3)', padding: 24 }}>Chargement…</div>}

      {!loading && (
        <div className="ta-cards">
          {filtered.map(c => {
            const short = ((c.Title || c.name || '')
              .split(/\s+/)
              .map(w => w[0])
              .join('')
              .slice(0, 3))
              .toUpperCase() || 'CL';
            return (
              <article key={c.id} className="ta-card" onClick={() => setSelected(c)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.2em' }}>{short}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.08em' }}>
                    {String(c.facturesCount).padStart(2, '0')} fact.
                  </div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 500, letterSpacing: '-0.015em', margin: '32px 0 6px', textWrap: 'pretty' }}>
                  {c.Title || c.name}
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.04em' }}>
                  {(c.City || c.city || '—')}{c.Country || c.country ? ` · ${c.Country || c.country}` : ''}
                </div>
                <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                  <div>
                    <div className="eyebrow">CA total</div>
                    <div className="mono tnum" style={{ fontSize: 20, color: 'var(--ink)', marginTop: 6 }}>{fmtEUR(c.ca)} €</div>
                  </div>
                  <span className="mono" style={{ color: 'var(--ink-4)', fontSize: 18 }}>→</span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {selected && (
        <CustomerDrawer
          client={selected}
          invoices={invoices}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setFormOpen(true); setSelected(null); }}
          onDelete={handleDelete}
        />
      )}

      {formOpen && (
        <CustomerForm
          customer={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSave={async (payload) => {
            try {
              if (editing) {
                await updateCustomer(editing.id, payload);
                setCustomers(customers.map(c => c.id === editing.id ? { ...c, ...payload } : c));
              } else {
                const id = await createCustomer({ ...payload, created: new Date() });
                setCustomers([...customers, { id, ...payload, created: new Date() }]);
              }
              setFormOpen(false);
              setEditing(null);
            } catch (e) { console.error(e); alert('Erreur'); }
          }}
        />
      )}
    </PageShell>
  );
};

function CustomerDrawer({ client, invoices, onClose, onEdit, onDelete }) {
  const clientInvoices = invoices.filter(i => {
    const name = typeof i.client === 'string' ? i.client : (i.client?.Title || '');
    return name === client.Title || name === client.name || i.clientId === client.id;
  });
  const ca = clientInvoices.reduce((a, i) => a + invoiceTotal(i), 0);

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div className="eyebrow" style={{ color: 'var(--accent)' }}>§ 04.A · Client</div>
          <button onClick={onClose} className="ta-icon-btn">{Ic.close}</button>
        </div>

        <div style={{ fontSize: 36, letterSpacing: '-0.02em', marginBottom: 8, textWrap: 'pretty' }}>
          {client.Title || client.name}
        </div>
        <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 12, letterSpacing: '0.08em', marginBottom: 32 }}>
          {client.Address || client.address || '—'}{client.City || client.city ? ` · ${client.City || client.city}` : ''}
          {client.ZipCode || client.zipCode ? ` · ${client.ZipCode || client.zipCode}` : ''}
        </div>

        <section className="ta-meta-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div><div className="k">Factures</div><div className="v">{clientInvoices.length}</div></div>
          <div><div className="k">CA total</div><div className="v tnum">{fmtEUR(ca)} €</div></div>
          <div><div className="k">Ville</div><div className="v">{client.City || client.city || '—'}</div></div>
        </section>

        <div className="eyebrow" style={{ marginTop: 32, marginBottom: 16 }}>§ Informations</div>
        <div style={{ borderTop: '1px solid var(--rule)' }}>
          {[
            ['Email', client.Email || client.email],
            ['Téléphone', client.Phone || client.phone],
            ['Adresse', client.Address || client.address],
            ['Ville', `${client.ZipCode || client.zipCode || ''} ${client.City || client.city || ''}`.trim()],
            ['SIRET', client.Siret || client.siret],
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--rule)' }}>
              <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{k}</span>
              <span style={{ fontSize: 13 }}>{v}</span>
            </div>
          ))}
        </div>

        <div className="eyebrow" style={{ marginTop: 32, marginBottom: 16 }}>§ Factures récentes</div>
        <div style={{ borderTop: '1px solid var(--rule)' }}>
          {clientInvoices.slice(0, 5).map(i => (
            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--rule)' }}>
              <div>
                <div style={{ fontSize: 13 }}>#{i.num}</div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }}>
                  {toDate(i.created)?.toLocaleDateString('fr-FR') || '—'}
                </div>
              </div>
              <div className="mono tnum" style={{ color: 'var(--accent)' }}>{fmtEUR(invoiceTotal(i))} €</div>
            </div>
          ))}
          {clientInvoices.length === 0 && <div className="mono" style={{ color: 'var(--ink-4)', padding: '14px 0' }}>Aucune facture</div>}
        </div>

        <div style={{ marginTop: 48, display: 'flex', gap: 10 }}>
          <Btn kind="ghost" onClick={onEdit}>Modifier</Btn>
          <Btn kind="danger" onClick={() => onDelete(client)}>Supprimer</Btn>
        </div>
      </div>
    </div>
  );
}

function CustomerForm({ customer, onClose, onSave }) {
  const [data, setData] = useState({
    Title: customer?.Title || customer?.name || '',
    Email: customer?.Email || customer?.email || '',
    Phone: customer?.Phone || customer?.phone || '',
    Address: customer?.Address || customer?.address || '',
    City: customer?.City || customer?.city || '',
    ZipCode: customer?.ZipCode || customer?.zipCode || '',
    Siret: customer?.Siret || customer?.siret || '',
  });
  const update = (field) => (e) => setData({ ...data, [field]: e.target.value });

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>§ 04.B · {customer ? 'Modifier' : 'Nouveau'} client</div>
            <div style={{ fontSize: 28, letterSpacing: '-0.02em', marginTop: 8 }}>{customer ? 'Modification' : 'Création'}</div>
          </div>
          <button onClick={onClose} className="ta-icon-btn">{Ic.close}</button>
        </div>

        {[
          ['Title', 'Nom / Raison sociale'],
          ['Email', 'Email'],
          ['Phone', 'Téléphone'],
          ['Address', 'Adresse'],
          ['ZipCode', 'Code postal'],
          ['City', 'Ville'],
          ['Siret', 'SIRET'],
        ].map(([k, l]) => (
          <div key={k} style={{ marginBottom: 24 }}>
            <label className="mono" style={{ display: 'block', fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>{l}</label>
            <input
              value={data[k]}
              onChange={update(k)}
              style={{ width: '100%', padding: '12px 0', background: 'transparent', border: 0, borderBottom: '1px solid var(--rule-2)', color: 'var(--ink)', fontSize: 14, outline: 'none', fontFamily: k === 'Email' || k === 'Phone' || k === 'Siret' ? 'var(--font-mono)' : 'var(--font-sans)' }}
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 32 }}>
          <Btn kind="ghost" onClick={onClose}>Annuler</Btn>
          <Btn kind="primary" onClick={() => {
            if (!data.Title) { alert('Nom requis'); return; }
            onSave(data);
          }}>{customer ? 'Enregistrer' : 'Créer'}</Btn>
        </div>
      </div>
    </div>
  );
}

export default Customers;
